import React, { useState, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import moment from "moment";

const useStyles = makeStyles()(theme => ({
    wrap: { position: "relative", width: "100%" },
    svg: { display: "block", width: "100%", height: "auto", overflow: "visible" },
    grid: { stroke: theme.palette.mode === "light" ? "#eeede7" : "#252523", strokeWidth: 1 },
    axisText: {
        fontSize: 10,
        fill: theme.palette.text.disabled,
        fontVariantNumeric: "tabular-nums",
    },
    dotRing: { stroke: theme.palette.mode === "light" ? "#ffffff" : "#1a1a19", strokeWidth: 2 },
    crosshair: {
        stroke: theme.palette.text.disabled,
        strokeWidth: 1,
        strokeDasharray: "3 3",
        pointerEvents: "none",
    },
    legend: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5, 2),
        marginBottom: theme.spacing(1),
        fontSize: 12,
    },
    legendItem: { display: "flex", alignItems: "center", gap: 6 },
    legendSwatch: { width: 9, height: 9, borderRadius: 2, flexShrink: 0 },
    tooltip: {
        position: "absolute",
        pointerEvents: "none",
        background: theme.palette.mode === "light" ? "#ffffff" : "#26262b",
        border: `1px solid ${theme.palette.mode === "light" ? "#dfe3ea" : "#33333a"}`,
        borderRadius: 6,
        padding: theme.spacing(0.75, 1),
        fontSize: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,.14)",
        whiteSpace: "nowrap",
        zIndex: 3,
        top: 0,
    },
    tipDay: { fontWeight: 700, marginBottom: 3 },
    tipRow: { display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" },
    tipNum: { fontVariantNumeric: "tabular-nums", fontWeight: 600, marginLeft: 10 },
    empty: { fontSize: 13, color: theme.palette.text.secondary, padding: theme.spacing(2, 0) },
}));

const W = 720;
const H = 170;
const AIRE = 0.9;
const PAD = { top: 12, right: 10, bottom: 22, left: 34 };

const curva = (pts) => {
    const n = pts.length;
    if (n === 0) return "";
    if (n === 1) return `M${pts[0].x},${pts[0].y}`;
    if (n === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;

    const d = [];
    for (let i = 0; i < n - 1; i++) {
        d.push((pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x));
    }

    const m = [d[0]];
    for (let i = 1; i < n - 1; i++) {
        m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2);
    }
    m.push(d[n - 2]);

    for (let i = 0; i < n - 1; i++) {
        if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
        const a = m[i] / d[i];
        const b = m[i + 1] / d[i];
        const s = a * a + b * b;
        if (s > 9) {
            const t = 3 / Math.sqrt(s);
            m[i] = t * a * d[i];
            m[i + 1] = t * b * d[i];
        }
    }

    let out = `M${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < n - 1; i++) {
        const h = (pts[i + 1].x - pts[i].x) / 3;
        out += ` C${pts[i].x + h},${pts[i].y + m[i] * h}` +
               ` ${pts[i + 1].x - h},${pts[i + 1].y - m[i + 1] * h}` +
               ` ${pts[i + 1].x},${pts[i + 1].y}`;
    }
    return out;
};

const MultiArea = ({ data, series, emptyLabel }) => {
    const { classes } = useStyles();
    const [hover, setHover] = useState(null);

    const calc = useMemo(() => {
        if (!data || data.length === 0 || series.length === 0) return null;

        const valor = (d, k) => d.valores[k] || 0;

        let max = 1;
        let granTotal = 0;
        for (const d of data) {
            for (const s of series) {
                const v = valor(d, s.key);
                granTotal += v;
                if (v > max) max = v;
            }
        }
        if (granTotal === 0) return { vacio: true };

        const innerW = W - PAD.left - PAD.right;
        const innerH = H - PAD.top - PAD.bottom;
        const base = PAD.top + innerH;
        const x = (i) => data.length === 1
            ? PAD.left + innerW / 2
            : PAD.left + (i / (data.length - 1)) * innerW;
        const y = (v) => base - (v / max) * innerH * AIRE;

        const orden = [...series].sort((a, b) => {
            const suma = (s) => data.reduce((acc, d) => acc + valor(d, s.key), 0);
            return suma(b) - suma(a);
        });

        const curvas = orden.map((s) => {
            const pts = data.map((d, i) => ({ x: x(i), y: y(valor(d, s.key)) }));
            const linea = curva(pts);
            return {
                key: s.key,
                color: s.color,
                linea,
                area: `${linea} L${pts[pts.length - 1].x},${base} L${pts[0].x},${base} Z`
            };
        });

        let pico = null;
        data.forEach((d, i) => {
            for (const s of series) {
                const v = valor(d, s.key);
                if (v > 0 && (!pico || v > pico.valor)) {
                    pico = { valor: v, i, color: s.color };
                }
            }
        });

        return { max, x, y, base, innerH, curvas, pico };
    }, [data, series]);

    if (!calc) return null;
    if (calc.vacio) return <div className={classes.empty}>{emptyLabel}</div>;

    const paso = Math.ceil(data.length / 7);

    const mover = (e) => {
        const caja = e.currentTarget.getBoundingClientRect();
        const rel = ((e.clientX - caja.left) / caja.width) * W;
        const innerW = W - PAD.left - PAD.right;
        const i = data.length === 1
            ? 0
            : Math.round(((rel - PAD.left) / innerW) * (data.length - 1));
        setHover(Math.max(0, Math.min(data.length - 1, i)));
    };

    const dia = hover != null ? data[hover] : null;

    return (
        <div className={classes.wrap}>
            <div className={classes.legend}>
                {series.map((s) => (
                    <span key={s.key} className={classes.legendItem}>
                        <i className={classes.legendSwatch} style={{ background: s.color }} />
                        {s.name}
                    </span>
                ))}
            </div>

            <svg
                className={classes.svg}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                onMouseMove={mover}
                onMouseLeave={() => setHover(null)}
                role="img"
                aria-label={series.map(s => s.name).join(", ")}
            >
                {[0, 0.5, 1].map((f) => {
                    const v = Math.round(calc.max * (1 - f));
                    const yy = calc.y(v);
                    return (
                        <g key={f}>
                            <line className={classes.grid} x1={PAD.left} x2={W - PAD.right} y1={yy} y2={yy} />
                            <text className={classes.axisText} x={0} y={yy + 3}>{v}</text>
                        </g>
                    );
                })}

                <defs>
                    {}
                    {calc.curvas.map((c, i) => (
                        <linearGradient key={c.key} id={`mul${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={c.color} stopOpacity="0.24" />
                            <stop offset="100%" stopColor={c.color} stopOpacity="0.02" />
                        </linearGradient>
                    ))}
                </defs>

                {calc.curvas.map((c, i) => (
                    <path key={`a${c.key}`} d={c.area} fill={`url(#mul${i})`} />
                ))}

                {calc.curvas.map((c) => (
                    <path
                        key={`l${c.key}`}
                        d={c.linea}
                        fill="none"
                        stroke={c.color}
                        strokeWidth={2}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                ))}

                {hover != null && (
                    <line className={classes.crosshair} x1={calc.x(hover)} x2={calc.x(hover)}
                          y1={PAD.top} y2={calc.base} />
                )}

                {}
                {calc.pico && (
                    <g>
                        <circle cx={calc.x(calc.pico.i)} cy={calc.y(calc.pico.valor)} r={4.5}
                                fill={calc.pico.color} className={classes.dotRing} />
                        <text x={calc.x(calc.pico.i)} y={calc.y(calc.pico.valor) - 9} textAnchor="middle"
                              className={classes.axisText} style={{ fontWeight: 700 }}>
                            {calc.pico.valor}
                        </text>
                    </g>
                )}

                {data.map((d, i) => (i % paso === 0 || i === data.length - 1) && (
                    <text key={i} className={classes.axisText} x={calc.x(i)} y={H - 6} textAnchor="middle">
                        {moment(d.day).format("DD/MM")}
                    </text>
                ))}
            </svg>

            {dia && (
                <div
                    className={classes.tooltip}
                    style={{
                        left: `${(calc.x(hover) / W) * 100}%`,
                        transform: hover > data.length / 2 ? "translateX(-105%)" : "translateX(5%)",
                    }}
                >
                    <div className={classes.tipDay}>{moment(dia.day).format("ddd DD/MM")}</div>
                    {series.map((s) => (
                        <div key={s.key} className={classes.tipRow}>
                            <span className={classes.legendItem}>
                                <i className={classes.legendSwatch} style={{ background: s.color }} />
                                {s.name}
                            </span>
                            <span className={classes.tipNum}>{dia.valores[s.key] || 0}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiArea;
